import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  BookOpen,
  ChevronLeft,
  Info,
  Loader2,
  MoreVertical,
  Printer,
  RefreshCw,
  Settings2,
  View,
} from 'lucide-react';

import { ReviewStreamTabs } from '../components/ReviewStreamTabs';
import { RevisionFamilyDeckWeb } from '../features/review/RevisionFamilyDeckWeb';
import { StreamLoadProgress } from '../features/review/StreamLoadProgress';
import { buildRevisionFamilyDeck } from '../features/review/reviewDeckBuilder';
import {
  PasDeliberatePracticePrintPreviewModal,
  RevisionPrintPreviewModal,
  buildRevisionPrintParticlesFromDeck,
  usePasDeliberatePracticePrintAction,
  useRevisionPrintAction,
  type RevisionPrintParticle,
} from '../features/review/print';
import { useAuth } from '../context/AuthContext';
import { useActiveSubject } from '../context/ActiveSubjectContext';
import {
  getDefaultStreamForSubject,
  getStreamDescription,
  getStreamLabel,
  isCoreSubjectCode,
  isPasDeliberatePracticeStream,
  isValidSubjectCode,
  SUBJECT_CONFIG,
  type LearningEpisodeStream,
  type SubjectCode,
} from '../utils/subjectConfig';
import {
  reviewService,
  findDailyReviewProgressEntry,
  buildLECDeckCursorFromCard,
  type DailyReviewProgress,
  type LearningEpisodePlan,
  type ReviewFeedbackKind,
  type ReviewHomeSurface,
} from '../services/reviewService';
import type { RevisionFamilyDeckCard } from '../features/review/reviewDeckBuilder';
import {
  blendStreamLoadEtaMs,
  hydrateStreamLoadEta,
  peekLocalStreamLoadEtaMs,
  recordStreamLoadDuration,
} from '../utils/streamLoadEta';

const EMPTY_PRINT_PARTICLES: RevisionPrintParticle[] = [];

type ReviewLocationState = {
  subject?: SubjectCode;
  stream?: LearningEpisodeStream;
};

export function ReviewSessionPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const routeState = (location.state || {}) as ReviewLocationState;
  const { activeSubject: currentSubject, setActiveSubject } = useActiveSubject();

  useEffect(() => {
    if (!isCoreSubjectCode(currentSubject)) {
      navigate('/', { replace: true });
    }
  }, [currentSubject, navigate]);

  const [currentStream, setCurrentStream] = useState<LearningEpisodeStream>(
    () => routeState.stream || getDefaultStreamForSubject(currentSubject),
  );
  const [surface, setSurface] = useState<ReviewHomeSurface | null>(null);
  const [plan, setPlan] = useState<LearningEpisodePlan | null>(null);
  const [cardIndex, setCardIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [streamLoadWait, setStreamLoadWait] = useState<{ startedAtMs: number; etaMs: number } | null>(null);
  const streamLoadWaitRef = useRef<{ startedAtMs: number; etaMs: number; subject: string; stream: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [leadDraft, setLeadDraft] = useState<Record<string, string>>({});
  const [familyDraft, setFamilyDraft] = useState<Record<string, { score?: number; feedback?: string }>>({});
  const [dailyReviewProgress, setDailyReviewProgress] = useState<DailyReviewProgress | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [aboutStreamOpen, setAboutStreamOpen] = useState(false);
  const [swapConfirmOpen, setSwapConfirmOpen] = useState(false);
  const [swapping, setSwapping] = useState(false);
  const [printSubQuestionsExpandedByParticleId, setPrintSubQuestionsExpandedByParticleId] = useState<Record<string, boolean>>({});
  const [subQuestionsSavingByParticleId, setSubQuestionsSavingByParticleId] = useState<Record<string, boolean>>({});
  const menuRef = useRef<HTMLDivElement | null>(null);

  const surfaceRequestSeqRef = useRef(0);
  const surfaceRequestContextRef = useRef({ subject: currentSubject, stream: currentStream });
  surfaceRequestContextRef.current = { subject: currentSubject, stream: currentStream };

  const beginSurfaceRequest = useCallback((subject: SubjectCode, stream: LearningEpisodeStream) => {
    surfaceRequestSeqRef.current += 1;
    return {
      id: surfaceRequestSeqRef.current,
      subject,
      stream,
    };
  }, []);

  const shouldCommitSurfaceRequest = useCallback((request: {
    id: number;
    subject: SubjectCode;
    stream: LearningEpisodeStream;
  }) => {
    const currentContext = surfaceRequestContextRef.current;
    return request.id === surfaceRequestSeqRef.current
      && request.subject === currentContext.subject
      && request.stream === currentContext.stream;
  }, []);

  const handleStreamChange = useCallback((stream: LearningEpisodeStream) => {
    setCurrentStream(stream);
    setCardIndex(0);
    setMessage(null);
    setError(null);
  }, []);

  const handleBuildingProgress = useCallback((progress: {
    buildStartedAt?: string;
    estimatedReadyMs?: number;
  }) => {
    const prev = streamLoadWaitRef.current;
    const startedFromServer = Date.parse(String(progress.buildStartedAt || ''));
    const startedAtMs = Number.isFinite(startedFromServer)
      ? Math.min(prev?.startedAtMs ?? startedFromServer, startedFromServer)
      : (prev?.startedAtMs ?? Date.now());
    const elapsed = Math.max(0, Date.now() - startedAtMs);
    const remaining = Number(progress.estimatedReadyMs);
    const serverTotal = Number.isFinite(remaining) && remaining > 0 ? elapsed + remaining : null;
    const local = peekLocalStreamLoadEtaMs(currentSubject, currentStream);
    const etaMs = blendStreamLoadEtaMs(serverTotal, local);
    streamLoadWaitRef.current = {
      startedAtMs,
      etaMs,
      subject: currentSubject,
      stream: currentStream,
    };
    setStreamLoadWait({ startedAtMs, etaMs });
  }, [currentSubject, currentStream]);

  useEffect(() => {
    if (loading) {
      if (streamLoadWaitRef.current) {
        return;
      }
      hydrateStreamLoadEta(currentSubject, currentStream);
      const local = peekLocalStreamLoadEtaMs(currentSubject, currentStream);
      const etaMs = blendStreamLoadEtaMs(null, local);
      const startedAtMs = Date.now();
      streamLoadWaitRef.current = {
        startedAtMs,
        etaMs,
        subject: currentSubject,
        stream: currentStream,
      };
      setStreamLoadWait({ startedAtMs, etaMs });
      return;
    }

    const finished = streamLoadWaitRef.current;
    streamLoadWaitRef.current = null;
    setStreamLoadWait(null);
    if (finished && !error) {
      recordStreamLoadDuration(finished.subject, finished.stream, Date.now() - finished.startedAtMs);
    }
  }, [currentStream, currentSubject, error, loading]);

  const loadDailyReviewProgress = useCallback(async (options?: {
    forceRefresh?: boolean;
    peekMissingSurfaces?: boolean;
  }) => {
    if (!user?.id) {
      return;
    }

    try {
      const nextProgress = await reviewService.loadDailyReviewProgress(user.id, {
        forceRefresh: options?.forceRefresh === true,
        peekMissingSurfaces: options?.peekMissingSurfaces ?? false,
      });
      setDailyReviewProgress(nextProgress);
    } catch {
      setDailyReviewProgress(null);
    }
  }, [user?.id]);

  const loadSurface = useCallback(async () => {
    if (!user?.id) return;
    const request = beginSurfaceRequest(currentSubject, currentStream);

    try {
      setError(null);
      const { rolled } = await reviewService.handleReviewProductDayRollover({
        userId: user.id,
        subject: currentSubject,
        activeStream: currentStream,
      });
      const peeked = rolled
        ? null
        : reviewService.peekHomeReviewSurface({
          userId: user.id,
          subjectFilter: currentSubject,
          stream: currentStream,
        });

      if (peeked) {
        if (shouldCommitSurfaceRequest(request)) {
          setSurface(peeked);
          setPlan(peeked.sequencePlan);
          setLoading(false);
        }
        const nextSurface = await reviewService.ensureLearningEpisodeSessionSurface(peeked);
        if (!shouldCommitSurfaceRequest(request)) {
          return;
        }
        setSurface(nextSurface);
        setPlan(nextSurface.sequencePlan);
        setCardIndex(0);
        void loadDailyReviewProgress();
        return;
      }

      setLoading(true);
      let raw = await reviewService.loadHomeReviewSurface({
        userId: user.id,
        subjectFilter: currentSubject,
        stream: currentStream,
        forceRefresh: rolled,
        preferSession: rolled ? false : undefined,
        onBuildingProgress: handleBuildingProgress,
      });

      if (!raw.sequencePlan && raw.unavailableState !== 'no_particles') {
        const progress = await reviewService.loadDailyReviewProgress(user.id, { forceRefresh: true });
        const entry = findDailyReviewProgressEntry(progress, currentSubject, currentStream);
        if (entry && !entry.isComplete && !entry.isInfinite) {
          try {
            await reviewService.ensureStreamLearningEpisodePlan({
              userId: user.id,
              subject: currentSubject,
              stream: currentStream,
            });
            raw = await reviewService.loadHomeReviewSurface({
              userId: user.id,
              subjectFilter: currentSubject,
              stream: currentStream,
              forceRefresh: true,
              onBuildingProgress: handleBuildingProgress,
            });
          } catch {
            // 保留首次 load 结果。
          }
        }
      }

      if (!shouldCommitSurfaceRequest(request)) {
        return;
      }
      const nextSurface = await reviewService.ensureLearningEpisodeSessionSurface(raw);
      if (!shouldCommitSurfaceRequest(request)) {
        return;
      }
      setSurface(nextSurface);
      setPlan(nextSurface.sequencePlan);
      setCardIndex(0);
      void loadDailyReviewProgress();
    } catch (loadError) {
      if (!shouldCommitSurfaceRequest(request)) {
        return;
      }
      setError(loadError instanceof Error ? loadError.message : '复习加载失败');
    } finally {
      if (shouldCommitSurfaceRequest(request)) {
        setLoading(false);
      }
    }
  }, [beginSurfaceRequest, currentStream, currentSubject, handleBuildingProgress, loadDailyReviewProgress, shouldCommitSurfaceRequest, user?.id]);

  const suppressSubjectResetRef = useRef(Boolean(routeState.stream || routeState.subject));
  const prevSubjectRef = useRef(currentSubject);

  useEffect(() => {
    if (routeState.subject && isValidSubjectCode(routeState.subject)) {
      setActiveSubject(routeState.subject);
    }
    if (routeState.stream) {
      setCurrentStream(routeState.stream);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (suppressSubjectResetRef.current) {
      suppressSubjectResetRef.current = false;
      prevSubjectRef.current = currentSubject;
      return;
    }
    if (prevSubjectRef.current === currentSubject) {
      return;
    }
    prevSubjectRef.current = currentSubject;
    setCurrentStream(getDefaultStreamForSubject(currentSubject));
    setCardIndex(0);
    setLeadDraft({});
    setFamilyDraft({});
    setMessage(null);
    setError(null);
  }, [currentSubject]);

  useEffect(() => {
    void loadSurface();
  }, [loadSurface]);

  useEffect(() => {
    void loadDailyReviewProgress();
  }, [loadDailyReviewProgress, currentSubject]);

  const particlesById = useMemo(() => {
    const map: Record<string, NonNullable<LearningEpisodePlan>['particles'][number]> = {};
    for (const particle of plan?.particles || []) {
      map[particle.id] = particle;
    }
    return map;
  }, [plan?.particles]);

  const deckCards = useMemo(
    () => (plan?.particles?.length ? buildRevisionFamilyDeck(plan.particles, currentStream) : []),
    [currentStream, plan?.particles],
  );

  const leadThoughtByParticleId = useMemo(() => {
    const next: Record<string, { text?: string }> = {};
    for (const [particleId, entry] of Object.entries(plan?.leadThoughtByParticleId || {})) {
      next[particleId] = { text: entry?.text || leadDraft[particleId] || '' };
    }
    for (const [particleId, text] of Object.entries(leadDraft)) {
      if (!next[particleId]) next[particleId] = { text };
    }
    return next;
  }, [leadDraft, plan?.leadThoughtByParticleId]);

  const familyFeedbackByParticleId = useMemo(() => {
    const next: Record<string, { score?: number | null; feedback?: string | null }> = {};
    for (const [particleId, entry] of Object.entries(plan?.familyFeedbackByParticleId || {})) {
      next[particleId] = {
        score: entry?.score ?? familyDraft[particleId]?.score ?? null,
        feedback: entry?.feedback ?? familyDraft[particleId]?.feedback ?? null,
      };
    }
    for (const [particleId, entry] of Object.entries(familyDraft)) {
      next[particleId] = { ...next[particleId], ...entry };
    }
    return next;
  }, [familyDraft, plan?.familyFeedbackByParticleId]);

  const persistDeckCursor = useCallback(async (nextIndex: number, card: RevisionFamilyDeckCard | undefined) => {
    if (!plan || !card) return;
    await reviewService.saveLearningEpisodeDeckCursor(plan, {
      particleId: card.particleId,
      cardId: card.id,
      familyCardIndex: card.familyCardIndex,
    });
    setCardIndex(nextIndex);
  }, [plan]);

  const handleReviewFeedback = useCallback(async (card: RevisionFamilyDeckCard, feedback: ReviewFeedbackKind) => {
    if (!plan) return;
    setMessage(null);
    setError(null);
    try {
      const feedbackLabel = feedback === 'proficient' ? '熟悉' : feedback === 'fuzzy' ? '模糊' : '忘记';
      const isMemoryProtocol = card.familyFeedbackProtocol === 'memory_per_card';
      // Never finalize mid-family: 子问题 only advances; final card completes the family.
      const shouldFinalizeFamily = Boolean(card.isFamilyFinalCard);
      const annotation = String(familyFeedbackByParticleId[card.particleId]?.feedback || '').trim();

      await reviewService.recordReviewFeedback(plan, card.particleId, feedback);

      if (!shouldFinalizeFamily) {
        setMessage(`已记录：${feedbackLabel}`);
        const nextIndex = Math.min(cardIndex + 1, deckCards.length - 1);
        await persistDeckCursor(nextIndex, deckCards[nextIndex]);
        return;
      }

      if (isMemoryProtocol) {
        await reviewService.saveLearningEpisodeFamilyFeedback(plan, {
          particleId: card.particleId,
          score: familyFeedbackByParticleId[card.particleId]?.score ?? (
            feedback === 'proficient' ? 92 : feedback === 'fuzzy' ? 70 : 35
          ),
          feedback: annotation || null,
        });
      }

      // Family + FSRS path (memory overview / family final card).
      await reviewService.completeFamilyReview(plan, {
        particleId: card.particleId,
        feedback,
        summaryText: annotation || (isMemoryProtocol ? `memory_triad:${feedback}` : `review_triad:${feedback}`),
        cardKind: card.cardKind,
        streamDecorationKind: card.streamDecorationKind,
      });

      const remainingParticles = plan.particles.filter((particle) => particle.id !== card.particleId);
      const isLastFamily = remainingParticles.length === 0;

      if (isLastFamily) {
        await reviewService.completeLearningEpisodeSession(plan, {
          completionScore: feedback === 'proficient' ? 78 : feedback === 'fuzzy' ? 58 : 28,
          completionFeedback: feedback,
          completionSummary: annotation || `刻意练习三档反馈：${feedbackLabel}`,
        });
        reviewService.clearLearningEpisodeSurface(plan.cacheKey);
        setPlan(null);
        setSurface((current) => (current ? { ...current, sequencePlan: null, totalPendingCount: 0 } : current));
        setMessage(`已记录：${feedbackLabel}（已写入 FSRS）`);
        void loadDailyReviewProgress();
        return;
      }

      const remainingCards = buildRevisionFamilyDeck(remainingParticles, currentStream);
      const nextFamilyStart = Math.max(0, cardIndex - (card.familyCardIndex || 0));
      const nextIndex = Math.min(nextFamilyStart, Math.max(remainingCards.length - 1, 0));
      const nextCard = remainingCards[nextIndex];

      await reviewService.persistReviewSequenceProgress(plan, {
        particleIds: remainingParticles.map((particle) => particle.id),
        reviewedCount: Math.max(0, plan.totalCount - remainingParticles.length),
        deckCursor: buildLECDeckCursorFromCard(nextCard),
      });

      const nextPlan: LearningEpisodePlan = {
        ...plan,
        particles: remainingParticles,
        currentIndex: 0,
        pendingCount: remainingParticles.length,
      };
      setPlan(nextPlan);
      setCardIndex(nextIndex);
      setMessage(`已记录：${feedbackLabel}`);
    } catch (feedbackError) {
      setError(feedbackError instanceof Error ? feedbackError.message : '反馈失败');
    }
  }, [cardIndex, currentStream, deckCards, familyFeedbackByParticleId, loadDailyReviewProgress, persistDeckCursor, plan]);

  const handleSubmitLeadThought = useCallback(async (card: RevisionFamilyDeckCard) => {
    if (!plan) return;
    const text = leadDraft[card.particleId] || leadThoughtByParticleId[card.particleId]?.text || '';
    await reviewService.saveLearningEpisodeLeadThought(plan, {
      particleId: card.particleId,
      text,
      inputMode: 'text',
    });
    const nextIndex = Math.min(cardIndex + 1, deckCards.length - 1);
    await persistDeckCursor(nextIndex, deckCards[nextIndex]);
  }, [cardIndex, deckCards, leadDraft, leadThoughtByParticleId, persistDeckCursor, plan]);

  const handleSubmitFamilySummary = useCallback(async (card: RevisionFamilyDeckCard) => {
    if (!plan) return;
    const draft = familyFeedbackByParticleId[card.particleId];
    await reviewService.saveLearningEpisodeFamilyFeedback(plan, {
      particleId: card.particleId,
      score: draft?.score ?? null,
      feedback: draft?.feedback ?? null,
    });
    await reviewService.completeFamilyReview(plan, {
      particleId: card.particleId,
      summaryScore: draft?.score ?? null,
      summaryText: draft?.feedback || 'Web 卡族总结完成',
    });
    const nextIndex = Math.min(cardIndex + 1, deckCards.length - 1);
    if (nextIndex === cardIndex && cardIndex >= deckCards.length - 1) {
      await reviewService.completeLearningEpisodeSession(plan, {
        completionScore: draft?.score ?? 78,
        completionSummary: 'Web LEC 复习完成',
      });
      setMessage('本轮复习已完成');
      return;
    }
    await persistDeckCursor(nextIndex, deckCards[nextIndex]);
  }, [cardIndex, deckCards, familyFeedbackByParticleId, persistDeckCursor, plan]);

  const currentCard = deckCards[cardIndex] || null;
  const usesDeliberatePracticePrint = isPasDeliberatePracticeStream(currentStream)
    || isPasDeliberatePracticeStream(plan?.request?.stream);

  const printParticles = useMemo(() => {
    if (!plan?.particles?.length) {
      return EMPTY_PRINT_PARTICLES;
    }
    const ordered = plan.particles.slice();
    if (currentCard?.particleId) {
      const currentIndex = ordered.findIndex((particle) => particle.id === currentCard.particleId);
      if (currentIndex > 0) {
        const [currentParticle] = ordered.splice(currentIndex, 1);
        ordered.unshift(currentParticle);
      }
    }
    const base = buildRevisionPrintParticlesFromDeck(ordered, currentStream, currentSubject);
    const feedbackMap = plan.familyFeedbackByParticleId;
    return base.map((particle) => {
      const storedExpanded = feedbackMap?.[particle.id]?.printSubQuestionsExpanded;
      const localExpanded = printSubQuestionsExpandedByParticleId[particle.id];
      if (currentSubject === 'math') {
        return {
          ...particle,
          subQuestionsExpanded: localExpanded !== undefined ? localExpanded : false,
        };
      }
      return {
        ...particle,
        subQuestionsExpanded: localExpanded !== undefined
          ? localExpanded
          : (storedExpanded != null ? storedExpanded : particle.subQuestionsExpanded !== false),
      };
    });
  }, [
    currentCard?.particleId,
    currentStream,
    currentSubject,
    plan?.familyFeedbackByParticleId,
    plan?.particles,
    printSubQuestionsExpandedByParticleId,
  ]);

  const {
    openPreview: openRevisionPrintPreview,
    closePreview: closeRevisionPrintPreview,
    previewVisible: revisionPrintPreviewVisible,
    print: printRevisionParticles,
    share: shareRevisionPrintPdf,
    printing: revisionPrintPending,
    sharing: revisionSharePending,
    printSessionId: revisionPrintSessionId,
    resolvingEmbeddedImages: revisionResolvingEmbeddedImages,
    resolvedParticles: revisionResolvedParticles,
  } = useRevisionPrintAction({
    particles: usesDeliberatePracticePrint ? EMPTY_PRINT_PARTICLES : printParticles,
    sessionId: plan?.sessionId,
    onNotice: setMessage,
  });

  const {
    openPreview: openPasDpPrintPreview,
    closePreview: closePasDpPrintPreview,
    previewVisible: pasDpPrintPreviewVisible,
    print: printPasDpParticles,
    share: sharePasDpPrintPdf,
    printing: pasDpPrintPending,
    sharing: pasDpSharePending,
    printSessionId: pasDpPrintSessionId,
    resolvingEmbeddedImages: pasDpResolvingEmbeddedImages,
    resolvedParticles: pasDpResolvedParticles,
  } = usePasDeliberatePracticePrintAction({
    particles: usesDeliberatePracticePrint ? printParticles : EMPTY_PRINT_PARTICLES,
    sessionId: plan?.sessionId,
    onNotice: setMessage,
  });

  const openPrintPreviewBase = usesDeliberatePracticePrint ? openPasDpPrintPreview : openRevisionPrintPreview;
  const openPrintPreview = useCallback(() => {
    if (currentSubject === 'math' && plan?.particles?.length) {
      const collapsed: Record<string, boolean> = {};
      plan.particles.forEach((particle) => {
        collapsed[particle.id] = false;
      });
      setPrintSubQuestionsExpandedByParticleId(collapsed);
    }
    openPrintPreviewBase();
  }, [currentSubject, openPrintPreviewBase, plan?.particles]);

  const closePrintPreview = usesDeliberatePracticePrint ? closePasDpPrintPreview : closeRevisionPrintPreview;
  const printPreviewVisible = usesDeliberatePracticePrint ? pasDpPrintPreviewVisible : revisionPrintPreviewVisible;
  const printPending = usesDeliberatePracticePrint ? pasDpPrintPending : revisionPrintPending;
  const sharePending = usesDeliberatePracticePrint ? pasDpSharePending : revisionSharePending;
  const printSessionId = usesDeliberatePracticePrint ? pasDpPrintSessionId : revisionPrintSessionId;
  const resolvingEmbeddedImages = usesDeliberatePracticePrint
    ? pasDpResolvingEmbeddedImages
    : revisionResolvingEmbeddedImages;
  const previewParticles = usesDeliberatePracticePrint ? pasDpResolvedParticles : revisionResolvedParticles;
  const runPrint = usesDeliberatePracticePrint ? printPasDpParticles : printRevisionParticles;
  const runSharePrintPdf = usesDeliberatePracticePrint ? sharePasDpPrintPdf : shareRevisionPrintPdf;

  const handleTogglePrintSubQuestions = useCallback(async (particleId: string, expanded: boolean) => {
    setPrintSubQuestionsExpandedByParticleId((current) => ({ ...current, [particleId]: expanded }));
    if (!plan || currentSubject === 'math') {
      return;
    }
    setSubQuestionsSavingByParticleId((current) => ({ ...current, [particleId]: true }));
    try {
      await reviewService.saveLearningEpisodeFamilyFeedback(plan, {
        particleId,
        printSubQuestionsExpanded: expanded,
      });
    } catch (toggleError) {
      setMessage(toggleError instanceof Error ? toggleError.message : '保存打印预览偏好失败');
    } finally {
      setSubQuestionsSavingByParticleId((current) => {
        const next = { ...current };
        delete next[particleId];
        return next;
      });
    }
  }, [currentSubject, plan]);

  const handleBulkTogglePrintSubQuestions = useCallback((expanded: boolean) => {
    if (!plan?.particles?.length) return;
    const next: Record<string, boolean> = {};
    plan.particles.forEach((particle) => {
      next[particle.id] = expanded;
    });
    setPrintSubQuestionsExpandedByParticleId(next);
    if (currentSubject === 'math') return;
    void Promise.all(plan.particles.map((particle) => (
      reviewService.saveLearningEpisodeFamilyFeedback(plan, {
        particleId: particle.id,
        printSubQuestionsExpanded: expanded,
      }).catch(() => undefined)
    )));
  }, [currentSubject, plan]);

  const handleSwapGroup = useCallback(async () => {
    if (!user?.id) return;
    setSwapping(true);
    setLoading(true);
    setSwapConfirmOpen(false);
    setMenuOpen(false);
    setError(null);
    setMessage(null);
    try {
      const next = await reviewService.swapStreamLearningEpisodeSurface({
        userId: user.id,
        subject: currentSubject,
        stream: currentStream,
        previousPlan: plan,
        onBuildingProgress: handleBuildingProgress,
      });
      setSurface(next);
      setPlan(next.sequencePlan);
      setCardIndex(0);
      setLeadDraft({});
      setFamilyDraft({});
      setPrintSubQuestionsExpandedByParticleId({});
      setMessage(next.sequencePlan ? '已换一组' : (next.unavailableReason || '暂无可用卡族'));
      void loadDailyReviewProgress({ forceRefresh: true });
    } catch (swapError) {
      setError(swapError instanceof Error ? swapError.message : '换一组失败');
    } finally {
      setSwapping(false);
      setLoading(false);
    }
  }, [currentStream, currentSubject, handleBuildingProgress, loadDailyReviewProgress, plan, user?.id]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onPointerDown = (event: MouseEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };
    window.addEventListener('mousedown', onPointerDown);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onPointerDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  const streamLabel = getStreamLabel(currentStream);
  const subjectLabel = SUBJECT_CONFIG[currentSubject].label;
  const printA11yLabel = usesDeliberatePracticePrint ? '打印刻意练习' : '打印实战答题纸';
  const learningSequenceLabel = plan?.episode?.providerCardEmissionMode === 'dynamic'
    ? '学习序列 · ∞族'
    : '学习序列';

  const headerActions = (
    <div className="flex items-center gap-1.5">
      <span className="mr-1 hidden text-sm text-muted sm:inline">{subjectLabel} · 可在顶部切换学科</span>
      <button
        type="button"
        onClick={openPrintPreview}
        className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-surface text-ink transition hover:border-primary-strong hover:bg-primary/5"
        aria-label={printA11yLabel}
        title={printA11yLabel}
      >
        <Printer size={16} strokeWidth={2.2} />
      </button>
      <div className="relative" ref={menuRef}>
        <button
          type="button"
          onClick={() => setMenuOpen((open) => !open)}
          className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-surface text-ink transition hover:border-primary-strong hover:bg-primary/5"
          aria-label="更多操作"
          aria-expanded={menuOpen}
        >
          <MoreVertical size={16} strokeWidth={2.2} />
        </button>
        {menuOpen ? (
          <div className="absolute right-0 z-40 mt-1 min-w-[220px] overflow-hidden rounded-xl border border-border bg-surface shadow-card">
            <button
              type="button"
              className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm text-ink hover:bg-canvas"
              onClick={() => {
                setMenuOpen(false);
                navigate('/settings/learning', { state: { subject: currentSubject, stream: currentStream } });
              }}
            >
              <Settings2 size={16} className="shrink-0 text-muted" />
              学习设置
            </button>
            <button
              type="button"
              className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm text-ink hover:bg-canvas"
              onClick={() => {
                setMenuOpen(false);
                if (!plan) {
                  setMessage('暂无学习序列');
                  return;
                }
                navigate('/review/sequence', {
                  state: {
                    subject: currentSubject,
                    stream: currentStream,
                    particleId: currentCard?.particleId || null,
                  },
                });
              }}
            >
              <View size={16} className="shrink-0 text-muted" />
              {learningSequenceLabel}
            </button>
            <button
              type="button"
              className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm text-ink hover:bg-canvas"
              onClick={() => {
                setMenuOpen(false);
                setAboutStreamOpen(true);
              }}
            >
              <Info size={16} className="shrink-0 text-muted" />
              关于{streamLabel}…
            </button>
            <div className="my-1 border-t border-border" />
            <button
              type="button"
              className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm text-ink hover:bg-canvas"
              onClick={() => {
                setMenuOpen(false);
                if (!currentCard?.particleId) {
                  setMessage('当前没有知识颗粒');
                  return;
                }
                navigate(`/knowledge/${encodeURIComponent(currentCard.particleId)}`);
              }}
            >
              <BookOpen size={16} className="shrink-0 text-muted" />
              知识颗粒详情
            </button>
            <button
              type="button"
              className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm text-ink hover:bg-canvas"
              onClick={() => {
                setMenuOpen(false);
                openPrintPreview();
              }}
            >
              <Printer size={16} className="shrink-0 text-muted" />
              {usesDeliberatePracticePrint ? '打印刻意练习' : '打印实战答题纸'}
            </button>
            <div className="my-1 border-t border-border" />
            <button
              type="button"
              disabled={swapping}
              className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm text-ink hover:bg-canvas disabled:opacity-50"
              onClick={() => {
                setMenuOpen(false);
                setSwapConfirmOpen(true);
              }}
            >
              <RefreshCw size={16} className={`shrink-0 text-muted ${swapping ? 'animate-spin' : ''}`} />
              换一组
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );

  const sessionDialogs = (
    <>
      {aboutStreamOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-4">
          <button type="button" className="absolute inset-0" aria-label="关闭" onClick={() => setAboutStreamOpen(false)} />
          <div className="relative z-10 w-full max-w-md rounded-2xl border border-border bg-surface p-5 shadow-card">
            <h3 className="text-base font-extrabold text-ink">关于{streamLabel}</h3>
            <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-muted">
              {getStreamDescription(currentStream)
                || '暂无说明。可在 AppServerConsole → Learning Episode Streams 中编辑该 Stream 的「前台说明」。'}
            </p>
            <div className="mt-4 flex justify-end">
              <button
                type="button"
                className="rounded-lg bg-primary px-4 py-2 text-sm text-white"
                onClick={() => setAboutStreamOpen(false)}
              >
                知道了
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {swapConfirmOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-4">
          <button type="button" className="absolute inset-0" aria-label="关闭" onClick={() => setSwapConfirmOpen(false)} />
          <div className="relative z-10 w-full max-w-md rounded-2xl border border-border bg-surface p-5 shadow-card">
            <h3 className="text-base font-extrabold text-ink">换一组？</h3>
            <p className="mt-3 text-sm leading-relaxed text-muted">
              将结束当前学习序列，并按同一 Stream 重新抽取一组题目。已复习进度会保留在足迹中。
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                className="rounded-lg border border-border px-4 py-2 text-sm"
                onClick={() => setSwapConfirmOpen(false)}
              >
                取消
              </button>
              <button
                type="button"
                className="rounded-lg bg-primary px-4 py-2 text-sm text-white"
                onClick={() => void handleSwapGroup()}
              >
                确认换一组
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {usesDeliberatePracticePrint ? (
        <PasDeliberatePracticePrintPreviewModal
          visible={printPreviewVisible}
          particles={previewParticles}
          resolvingEmbeddedImages={resolvingEmbeddedImages}
          sessionId={plan?.sessionId}
          printSessionId={printSessionId}
          printing={printPending}
          sharing={sharePending}
          onClose={closePrintPreview}
          onPrint={() => void runPrint()}
          onShare={() => void runSharePrintPdf()}
        />
      ) : (
        <RevisionPrintPreviewModal
          visible={printPreviewVisible}
          particles={previewParticles}
          resolvingEmbeddedImages={resolvingEmbeddedImages}
          sessionId={plan?.sessionId}
          printSessionId={printSessionId}
          printing={printPending}
          sharing={sharePending}
          subQuestionsSavingByParticleId={subQuestionsSavingByParticleId}
          onClose={closePrintPreview}
          onPrint={() => void runPrint()}
          onShare={() => void runSharePrintPdf()}
          onToggleSubQuestionsExpanded={(particleId, expanded) => {
            void handleTogglePrintSubQuestions(particleId, expanded);
          }}
          onBulkToggleSubQuestionsExpanded={handleBulkTogglePrintSubQuestions}
        />
      )}
    </>
  );

  if (loading && !plan) {
    return (
      <div className="flex justify-center py-16">
        {streamLoadWait ? (
          <StreamLoadProgress
            label={`正在准备「${getStreamLabel(currentStream)}」…`}
            startedAtMs={streamLoadWait.startedAtMs}
            etaMs={streamLoadWait.etaMs}
          />
        ) : (
          <Loader2 className="animate-spin text-primary" />
        )}
      </div>
    );
  }

  if (error && !plan) {
    return (
      <div className="mx-auto max-w-3xl space-y-4">
        <p className="text-danger">{error}</p>
        <Link to="/review" className="text-primary">返回复习入口</Link>
      </div>
    );
  }

  if (!plan || deckCards.length === 0) {
    return (
      <div className="mx-auto max-w-3xl space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <button type="button" onClick={() => navigate('/review')} className="inline-flex items-center gap-1 text-sm text-primary">
            <ChevronLeft size={16} />
            复习入口
          </button>
          {headerActions}
        </div>
        <ReviewStreamTabs
          subject={currentSubject}
          stream={currentStream}
          onStreamChange={handleStreamChange}
          dailyReviewProgress={dailyReviewProgress}
        />
        <p className="text-muted">{surface?.unavailableReason || '暂无复习卡片。'}</p>
        <Link to="/review" className="text-primary">返回复习入口</Link>
        {sessionDialogs}
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <button type="button" onClick={() => navigate('/review')} className="inline-flex items-center gap-1 text-sm text-primary">
          <ChevronLeft size={16} />
          复习入口
        </button>
        {headerActions}
      </div>

      <ReviewStreamTabs
        subject={currentSubject}
        stream={currentStream}
        onStreamChange={handleStreamChange}
        dailyReviewProgress={dailyReviewProgress}
      />

      {loading ? (
        <div className="flex justify-center py-8">
          {streamLoadWait ? (
            <StreamLoadProgress
              label={swapping ? '正在换一组…' : `正在准备「${getStreamLabel(currentStream)}」…`}
              startedAtMs={streamLoadWait.startedAtMs}
              etaMs={streamLoadWait.etaMs}
            />
          ) : (
            <Loader2 className="animate-spin text-primary" />
          )}
        </div>
      ) : (
        <RevisionFamilyDeckWeb
          cards={deckCards}
          currentIndex={cardIndex}
          onIndexChange={(index) => void persistDeckCursor(index, deckCards[index])}
          particlesById={particlesById}
          leadThoughtByParticleId={leadThoughtByParticleId}
          familyFeedbackByParticleId={familyFeedbackByParticleId}
          onLeadThoughtChange={(particleId, text) => setLeadDraft((current) => ({ ...current, [particleId]: text }))}
          onSubmitLeadThought={handleSubmitLeadThought}
          onFamilySummaryChange={(particleId, text) => setFamilyDraft((current) => ({
            ...current,
            [particleId]: { ...current[particleId], feedback: text },
          }))}
          onFamilySummaryScore={(particleId, score) => setFamilyDraft((current) => ({
            ...current,
            [particleId]: { ...current[particleId], score },
          }))}
          onSubmitFamilySummary={handleSubmitFamilySummary}
          onReviewFeedback={handleReviewFeedback}
        />
      )}

      {message ? <p className="text-sm text-success">{message}</p> : null}
      {error ? <p className="text-sm text-danger">{error}</p> : null}
      {sessionDialogs}
    </div>
  );
}
